<template>
  <form class="chamados-form" @submit.prevent="handleSubmit">
    <div class="form-grid">
      <div class="form-field">
        <select-list
          v-model="form.franquia_id"
          elem-id="chamados-franquia"
          :label="t('Franquia')"
          :itens-select="franquiasSelect"
          :required="true"
          data-testid="chamados-select-franquia"
        />
      </div>

      <div class="form-field">
        <select-list
          v-model="form.prioridade"
          elem-id="chamados-prioridade"
          :label="t('Prioridade')"
          :itens-select="prioridadesSelect"
          :required="true"
          data-testid="chamados-select-prioridade"
        />
      </div>
    </div>

    <div class="form-field">
      <input-text
        v-model="form.assunto"
        elem-id="chamados-assunto"
        :label="t('Assunto')"
        :required="true"
        :maxlength="MAX_ASSUNTO"
        data-testid="chamados-input-assunto"
      />
      <small class="contador" :class="{ 'contador--cheio': assuntoNoLimite }">
        {{ form.assunto.length }} / {{ MAX_ASSUNTO }}
      </small>
    </div>

    <div class="form-field">
      <!--
        O MESMO controle do formulário do Odoo (`InputRichText` com barra de
        formatação): negrito, listas, botão de imagem, colar e arrastar.
        A diferença é onde a imagem vai parar — `:external-images="true"`
        manda o arquivo para os anexos (Blob) em vez de embutir base64 no
        HTML. Base64 aqui truncaria a descrição em silêncio (`descricao` é
        TEXT, sql_mode não-estrito), e o usuário perderia o texto junto.
      -->
      <InputRichText
        v-model="form.descricao"
        elem-id="chamados-descricao"
        :label="t('Descreva o problema')"
        :placeholder="t('Descreva o problema em detalhes. Você pode colar imagens.')"
        :show-toolbar="true"
        :ng-required="true"
        :external-images="true"
        :disabled="enviando"
        :explanation="enviando ? EX.opening : null"
        min-height="200px"
        data-testid="chamados-input-descricao"
        @image-file="receberImagem"
      >
        <!--
          O anexo vive DENTRO do campo, como no formulário do Odoo: o rodapé
          divide a mesma caixa do editor. Fora dela, a imagem parecia outro
          assunto, e não parte desta mensagem.
        -->
        <template #rodape>
          <ChamadosImagePicker
            ref="pickerRef"
            v-model="anexos"
            :disabled="enviando"
            :explanation="enviando ? EX.opening : null"
            :dica="t('Cole, arraste ou use o botão de imagem — um print resolve o chamado em metade do tempo')"
            testid="chamados-anexo-abertura"
          />
        </template>
      </InputRichText>
    </div>

    <!--
      CONTEXTO AUTOMÁTICO — copiado do formulário do Odoo, e não é enfeite.
      Mostrar o que vai junto sem o usuário digitar faz duas coisas: ele para
      de repetir na descrição o que o sistema já sabe, e descobre ANTES de
      enviar se a tela capturada não é a que ele quer relatar.
    -->
    <div class="contexto">
      <div class="contexto-titulo">
        <i class="pi pi-info-circle"></i>
        {{ t('Informações incluídas automaticamente') }}:
      </div>
      <div class="contexto-itens">
        <span><strong>{{ t('Usuário') }}:</strong> {{ nomeDoUsuario }}</span>
        <span><strong>{{ t('Página') }}:</strong> {{ paginaResumida }}</span>
      </div>
    </div>
  </form>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

import ChamadosImagePicker from './ChamadosImagePicker.vue';
import { ticketExplanations } from './explanations';
import InputRichText from '../../inputs/InputRichText.vue';
import { eventBus } from '../../eventBus';
import { log } from '../../utils/DebugLogger';
import { permissionsService } from '../../services/permissionsService';
import { MAX_ASSUNTO, useChamados, type FranquiaDoSeletor } from '../../composables/useChamados';

/**
 * ChamadosForm — abrir um chamado novo.
 *
 * ---------------------------------------------------------------------------
 * POR QUE ESTE FORMULÁRIO **NÃO** USA `<FranchiseSelect>`
 * ---------------------------------------------------------------------------
 * `FranchiseSelect` é o seletor canônico da plataforma (usado em ~25 domínios) e
 * a regra da casa é reusá-lo. Aqui ele é a escolha ERRADA, e a razão é
 * verificável no próprio componente: ele carrega as franquias do FILTRO GLOBAL
 * (`useFiltro`, e o docblock dele diz isso na primeira linha).
 *
 * O filtro devolve as franquias LIBERADAS POR BILLING. Numa tela de suporte
 * isso é um defeito grave: a franquia com assinatura bloqueada abriria o
 * formulário com o seletor VAZIO e não conseguiria pedir socorro exatamente no
 * dia em que mais precisa ("meu sistema travou, não consigo faturar").
 *
 * Por isso a lista do seletor é alimentada por `/chamados/franquias`, que lê da
 * IDENTIDADE do usuário — a MESMA fonte que o `abrir()` do backend valida.
 *
 * Isto é desvio consciente do padrão, não desconhecimento dele.
 */

const emit = defineEmits<{
  criado: [id: number];
}>();

const { t } = useI18n();

const EX = computed(() => ticketExplanations(t));
const route = useRoute();
const { carregarFranquias, abrirChamado, anexarNaUltimaMensagem } = useChamados();

/**
 * A tela de ORIGEM do chamado, capturada no MOMENTO EM QUE O FORMULÁRIO MONTA.
 *
 * Congelada num `ref` de propósito, e não lida de `route.fullPath` na hora de
 * enviar: o modal abre POR CIMA da tela atual, mas nada impede o usuário de
 * navegar antes de terminar de escrever (o modal do assistente faz isso). Ler
 * na hora do envio gravaria a tela para onde ele FOI, não aquela onde o
 * problema aconteceu — que é a única que interessa ao suporte.
 *
 * É a informação que o suporte mais perde tempo pedindo, e a que o usuário
 * pior responde, porque quando perguntam ele já saiu de lá.
 */
const paginaDeOrigem = ref<string>('');

const enviando = ref(false);
const franquias = ref<FranquiaDoSeletor[]>([]);

/**
 * As imagens escolhidas, ainda em memória. Só sobem DEPOIS que o chamado
 * existe — é a mensagem criada por `abrir()` que lhes dá dono.
 */
const anexos = ref<File[]>([]);

/**
 * Imagem colada, arrastada ou escolhida DENTRO do editor.
 *
 * Cai na mesma lista do seletor de anexos — os dois caminhos são a mesma
 * coisa para o usuário, e ter duas listas faria uma delas ser esquecida no
 * envio. O `ChamadosImagePicker` cuida de comprimir, validar e mostrar a
 * miniatura, então aqui basta repassar o arquivo.
 */
const receberImagem = (arquivo: File): void => {
  pickerRef.value?.receber(arquivo);
};

const pickerRef = ref<InstanceType<typeof ChamadosImagePicker> | null>(null);

const form = reactive({
  franquia_id: null as number | null,
  assunto: '',
  descricao: '',
  prioridade: 'normal',
});

const franquiasSelect = computed(() =>
  franquias.value.map((f) => ({ ID: f.id, Name: f.nome })),
);

const prioridadesSelect = computed(() => [
  { ID: 'baixa', Name: t('Baixa') },
  { ID: 'normal', Name: t('Normal') },
  { ID: 'alta', Name: t('Alta') },
  { ID: 'critica', Name: t('Crítica') },
]);

const assuntoNoLimite = computed(() => form.assunto.length >= MAX_ASSUNTO);

/** Quem está abrindo — o mesmo fallback do formulário do Odoo. */
const nomeDoUsuario = computed<string>(() => {
  const u = permissionsService.getUser() as { name?: string; email?: string } | null;

  return u?.name || u?.email || t('Usuário');
});

/**
 * A rota, encurtada para caber na linha.
 *
 * Corta em 50 caracteres como o Odoo faz: o valor CHEIO é o que viaja no POST
 * — o corte é só de exibição. Truncar o que se envia gravaria um caminho pela
 * metade, que confunde mais do que ajuda.
 */
const paginaResumida = computed<string>(() => {
  const uri = paginaDeOrigem.value;
  if (!uri) return '—';

  return uri.length > 50 ? uri.slice(0, 50) + '...' : uri;
});

/**
 * O editor rich text NUNCA devolve string vazia: um campo em branco vale
 * `<p></p>`. Sem tirar as tags antes de medir, o botão de enviar acenderia com
 * a descrição vazia e o chamado chegaria ao suporte sem nada escrito — que é
 * exatamente o que o formulário do Odoo evita com o mesmo `replace`.
 */
const semTags = (html: string): string => html.replace(/<[^>]*>/g, '').trim();

const podeEnviar = computed(
  () =>
    !enviando.value &&
    form.franquia_id !== null &&
    form.assunto.trim() !== '' &&
    semTags(form.descricao) !== '',
);

onMounted(async () => {
  paginaDeOrigem.value = route.fullPath ?? '';

  try {
    franquias.value = await carregarFranquias();

    // Uma franquia só: escolhe sozinho. Obrigar o clique num seletor de uma
    // opção é ruído para a maioria dos franqueados, que tem exatamente uma.
    if (franquias.value.length === 1) {
      form.franquia_id = franquias.value[0].id;
    }
  } catch (e) {
    const detalhe = e instanceof Error ? e.message : String(e);
    eventBus.emit('globalMessage.error', t('Erro ao carregar as franquias') + ': ' + detalhe);
  }
});

const handleSubmit = async (): Promise<void> => {
  if (!podeEnviar.value || form.franquia_id === null) {
    // Emitido para a tela poder reagir sem inspecionar o estado interno do
    // formulário — a mesma convenção dos outros *Form.vue do sistema.
    eventBus.emit('form.validation.failed', { form: 'chamados', reason: 'campos_obrigatorios' });
    return;
  }

  enviando.value = true;

  try {
    const chamado = await abrirChamado({
      franquia_id: form.franquia_id,
      assunto: form.assunto.trim(),
      descricao: form.descricao,
      prioridade: form.prioridade,
      // `undefined` e nao `null`: o contrato gerado marca o campo como
      // OPCIONAL (`pagina_uri?: string`), e mandar null seria um valor que o
      // tipo nao admite. O vue-tsc pegou isso na hora.
      pagina_uri: paginaDeOrigem.value || undefined,
    });

    log('ChamadosForm', 'handleSubmit', 'Chamado aberto', chamado.id);

    // O CHAMADO JÁ ESTÁ ABERTO. Daqui para baixo, falha de imagem NÃO pode
    // desfazer nem esconder isso: perder o texto do problema porque um upload
    // caiu seria trocar um defeito pequeno por um grande. Por isso o `catch`
    // separado, com aviso próprio — o usuário fica sabendo que a imagem não
    // subiu e ainda assim tem o chamado aberto para responder com ela depois.
    if (anexos.value.length > 0) {
      try {
        await anexarNaUltimaMensagem(chamado.id, anexos.value);
      } catch (e) {
        const detalhe = e instanceof Error ? e.message : String(e);
        eventBus.emit(
          'globalMessage.error',
          t('O chamado foi aberto, mas a imagem não subiu') + ': ' + detalhe,
        );
      }
    }

    // O rodapé do Modal observa `*.saved` para saber que o salvamento teve
    // efeito; sem este evento ele acusaria "modal.save.no-effect" num chamado
    // que abriu certo.
    eventBus.emit('chamados.saved', { id: chamado.id });
    eventBus.emit('globalMessage.success', t('Chamado aberto com sucesso'));
    emit('criado', chamado.id);
  } catch (e) {
    const detalhe = e instanceof Error ? e.message : String(e);
    eventBus.emit('globalMessage.error', t('Erro ao abrir o chamado') + ': ' + detalhe);
  } finally {
    enviando.value = false;
  }
};
/*
 * O RODAPÉ É DO MODAL (chamado 2026-0031).
 *
 * "Cancelar" e "Abrir chamado" são os botões do footer de `src/ui/Modal.vue`,
 * que os desenha a partir de `:close-label` / `:save-label` / `:save-fn`. O
 * formulário não pode desenhá-los no corpo sem duplicar o rodapé da janela,
 * então expõe o que o modal precisa: disparar o envio e saber se o botão pode
 * acender. O `<form @submit>` continua existindo — é por ele que o Enter no
 * campo envia, e é o caminho que os testes exercitam.
 */
defineExpose({
  submit: handleSubmit,
  // Função, e não o computed: exposto como valor, `podeEnviar` chegaria ao
  // modal como ComputedRef no tipo, e `!ref` seria sempre falso — o botão
  // acenderia com o formulário vazio.
  podeEnviar: (): boolean => podeEnviar.value,
});
</script>

<style scoped>
.chamados-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.form-field {
  display: flex;
  flex-direction: column;
}

.contador {
  align-self: flex-end;
  font-size: 0.75rem;
  color: #777;
}

.contador--cheio {
  color: #c62828;
  font-weight: 600;
}

.contexto {
  background: #f6f8fa;
  border: 1px solid #e6e9ec;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 0.8rem;
  color: #555;
}

.contexto-titulo {
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 600;
  margin-bottom: 6px;
}

.contexto-itens {
  display: flex;
  flex-direction: column;
  gap: 3px;
  word-break: break-all;
}


@media (max-width: 768px) {
  .form-grid {
    grid-template-columns: 1fr;
  }
}
</style>
